"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = fetchHistoricalPrices;

var _findInvalidPrice = _interopRequireDefault(require("./find-invalid-price"));

var _getLimitToFetch = _interopRequireDefault(require("./get-limit-to-fetch"));

var _tryToLoadCache = _interopRequireDefault(require("./try-to-load-cache"));

var _dayjs = _interopRequireDefault(require("./dayjs"));

var _constants = require("./constants");

var _cropHistory = _interopRequireDefault(require("./crop-history"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

const runtimeCacheDefault = new Map();

const getRuntimeCacheKeyDefault = ({
  fiatTicker,
  assetTicker,
  granularity
}) => `${assetTicker}_${fiatTicker}_${granularity}`;

async function fetchHistoricalPrices({
  api,
  assetTickers,
  fiatTicker,
  granularity = 'day',
  getCacheFromStorage,
  requestLimit,
  timestamp,
  getCurrentTime = () => Date.now(),
  ignoreInvalidSymbols = false,
  ignoreCache = false,
  runtimeCache = runtimeCacheDefault,
  getRuntimeCacheKey = getRuntimeCacheKeyDefault
}) {
  const specificTimestamp = timestamp ? (0, _dayjs.default)(timestamp).utc().startOf(granularity).valueOf() : null;

  const requestTimestamp = specificTimestamp || _dayjs.default.utc(getCurrentTime()).subtract(1, granularity).startOf(granularity).valueOf();

  const historicalPricesMap = new Map();
  const fetchedPricesMap = new Map();
  const assetTickersToFetch = [];
  const tickerSymbols = Array.isArray(assetTickers) ? assetTickers : [assetTickers];

  for (const tickerSymbol of tickerSymbols) {
    const runtimeCacheKey = getRuntimeCacheKey({
      fiatTicker,
      assetTicker: tickerSymbol,
      granularity
    });
    const history = await (0, _tryToLoadCache.default)({
      runtimeCacheKey,
      runtimeCache,
      getCacheFromStorage: () => getCacheFromStorage(tickerSymbol)
    });
    historicalPricesMap.set(tickerSymbol, history);

    if (specificTimestamp && history.get(specificTimestamp)) {
      continue;
    }

    const limit = (0, _getLimitToFetch.default)({
      granularity,
      history: !ignoreCache ? history : new Map(),
      requestLimit,
      requestTimestamp,
      specificTimestamp
    });

    if (limit <= 0) {
      continue;
    }

    const lastCachedTime = [...history.keys()].sort().pop();
    const lastCachedItem = history.get(lastCachedTime);
    assetTickersToFetch.push({
      tickerSymbol,
      limit,
      lastCachedItem
    });
  }

  if (assetTickersToFetch.length > 0) {
    const maxLimit = Math.max(...assetTickersToFetch.map(({
      limit
    }) => limit));
    const requests = [assetTickersToFetch.filter(({
      limit
    }) => limit !== maxLimit), assetTickersToFetch.filter(({
      limit
    }) => limit === maxLimit)];
    await Promise.all(requests.filter(tickers => tickers.length > 0).map(async currentTickersToFetch => {
      const limit = Math.max(...currentTickersToFetch.map(({
        limit
      }) => limit));
      const requestParams = {
        assets: currentTickersToFetch.map(item => item.tickerSymbol),
        fiatArray: [fiatTicker],
        granularity: granularity,
        limit,
        timestamp: requestTimestamp / 1000,
        ignoreInvalidSymbols: ignoreInvalidSymbols
      };
      const fetchedPrices = await api(requestParams);
      Object.keys(fetchedPrices).forEach(assetTicker => {
        if (assetTicker === 'requestErrors') {
          const errors = fetchedPrices[assetTicker];
          Object.keys(errors).forEach(key => {
            if (key !== 'invalidCryptoSymbols' || !ignoreInvalidSymbols) {
              console.warn(`pricing-server: ${key}: `, errors[key]);
            }
          });
          return;
        }

        const newData = fetchedPrices[assetTicker][fiatTicker];
        const {
          lastCachedItem
        } = assetTickersToFetch.find(({
          tickerSymbol
        }) => tickerSymbol === assetTicker) || {};
        const wrongDataItem = (0, _findInvalidPrice.default)(newData, lastCachedItem);

        if (wrongDataItem) {
          const date = new Date(wrongDataItem.time * 1000);
          console.warn(`pricing-server: invalid ${assetTicker} price for ${date.toISOString()}: ${JSON.stringify(wrongDataItem)}`);
          return;
        }

        const filteredData = newData.filter(piece => piece.close !== 0).map(piece => ({
          time: piece.time,
          close: piece.close
        }));
        let history = historicalPricesMap.get(assetTicker) || new Map();

        for (let price of filteredData) {
          history.set(price.time * 1000, {
            close: price.close
          });
        }

        if (granularity === 'hour' && !specificTimestamp && filteredData.length > 0 && requestLimit) {
          history = (0, _cropHistory.default)({
            history,
            hourlyLimit: requestLimit || _constants.LIMIT[granularity]
          });
        }

        fetchedPricesMap.set(assetTicker, history);
        historicalPricesMap.set(assetTicker, history);
      });
    }));
  }

  for (const tickerSymbol of tickerSymbols) {
    const runtimeCacheKey = getRuntimeCacheKey({
      fiatTicker,
      assetTicker: tickerSymbol,
      granularity
    });
    const history = historicalPricesMap.get(tickerSymbol);

    if (history && history.size !== 0) {
      runtimeCache.set(runtimeCacheKey, [...history]);
    }
  }

  return {
    historicalPricesMap,
    fetchedPricesMap
  };
}